import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  UserRound,
  LogOut,
  ArrowLeft,
  Menu,
  X,
  Lock,
  Briefcase,
  Users,
  Languages,
  ChevronDown,
  Home,
  MessageSquare,
  Scale,
  FileCheck,
  Plus,
  CreditCard,
  FileStack,
  FileText,
  Bell,
  BarChart,
  Settings,
  ListTree,
  Truck,
  UserCog,
  Star,

} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { adminApi, clientApi, translatorApi, verifierApi } from '../../api/client';
import DashModal from './DashModal';
import PasswordChangeForm from './PasswordChangeForm';
import './DashboardLayout.css';
import './DashToolbar.css';

const NAV = {
  client: [
    { to: '/app', end: true, icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/app/new-order', icon: Plus, label: 'Commander en ligne' },
    { to: '/app/orders', icon: Package, label: 'Mes commandes' },
    { to: '/app/documents', icon: FileStack, label: 'Mes documents' },
    { to: '/app/payments', icon: CreditCard, label: 'Mes paiements' },
    { to: '/app/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/app/notifications', icon: Bell, label: 'Notifications' },
    { to: '/app/profile', icon: UserRound, label: 'Profil' },
  ],
  translator: [
    { to: '/pro', end: true, icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/pro/missions', icon: Briefcase, label: 'Mes missions' },
    { to: '/pro/availability', icon: UserCog, label: 'Disponibilité' },
    { to: '/pro/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/pro/notifications', icon: Bell, label: 'Notifications' },
    { to: '/pro/profile', icon: UserRound, label: 'Profil' },
  ],
  admin: [
    { to: '/admin', end: true, icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/admin/orders', icon: Package, label: 'Commandes' },
    { to: '/admin/clients', icon: Users, label: 'Clients' },
    { to: '/admin/translators', icon: Languages, label: 'Traducteurs & Interprètes' },
    { to: '/admin/verifiers', icon: Scale, label: 'Vérificateurs' },
    { to: '/admin/payments', icon: CreditCard, label: 'Paiements' },
    { to: '/admin/avis', icon: Star, label: 'Avis et évaluations' },
    { to: '/admin/services', icon: ListTree, label: 'Services' },
    { to: '/admin/languages', icon: Languages, label: 'Langues' },
    { to: '/admin/delivery', icon: Truck, label: 'Livraison' },
    { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/admin/email-logs', icon: FileText, label: 'Historique des e-mails' },
    { to: '/admin/notifications', icon: Bell, label: 'Notifications' },
    { to: '/admin/reports', icon: BarChart, label: 'Rapports' },
    { to: '/admin/profile', icon: UserRound, label: 'Profil' },
  ],
  legal: [
    { to: '/legal', end: true, icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/legal/verifications', icon: FileCheck, label: 'Mes vérifications' },
    { to: '/legal/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/legal/notifications', icon: Bell, label: 'Notifications' },
    { to: '/legal/profile', icon: UserRound, label: 'Profil' },
  ],
  lawyer: [
    { to: '/lawyer', end: true, icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/lawyer/verifications', icon: FileCheck, label: 'Mes vérifications' },
    { to: '/lawyer/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/lawyer/notifications', icon: Bell, label: 'Notifications' },
    { to: '/lawyer/profile', icon: UserRound, label: 'Profil' },
  ],
  notary: [
    { to: '/notary', end: true, icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/notary/verifications', icon: FileCheck, label: 'Mes vérifications' },
    { to: '/notary/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/notary/notifications', icon: Bell, label: 'Notifications' },
    { to: '/notary/profile', icon: UserRound, label: 'Profil' },
  ],
};

const PROFILE_PATH = {
  client: '/app/profile',
  translator: '/pro/profile',
  admin: '/admin/profile',
  legal: '/legal/profile',
  lawyer: '/lawyer/profile',
  notary: '/notary/profile',
};

const NOTIFICATIONS_PATH = {
  client: '/app/notifications',
  translator: '/pro/notifications',
  admin: '/admin/notifications',
  legal: '/legal/notifications',
  lawyer: '/lawyer/notifications',
  notary: '/notary/notifications',
};

const NOTIFICATIONS_API = {
  client: clientApi,
  translator: translatorApi,
  admin: adminApi,
  legal: verifierApi,
  lawyer: verifierApi,
  notary: verifierApi,
};

export default function DashboardLayout({ space }) {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const userMenuRef = useRef(null);
  const d = t.dashboard;
  const s = d[space] || d.legal || {};
  const items = NAV[space] || [];
  const notificationsPath = NOTIFICATIONS_PATH[space];
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const location = useLocation();
  const notifCount = notifs.filter((n) => !n.readAt).length;
  const roleLabel = d[`role${space[0].toUpperCase()}${space.slice(1)}`] || (space === 'lawyer' || space === 'notary' ? d.roleLegal : '') || space;

  useEffect(() => {
    const apiObj = NOTIFICATIONS_API[space];
    if (!apiObj?.notifications) return;
    let mounted = true;
    const load = () => {
      apiObj
        .notifications()
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.notifications;
          if (mounted) setNotifs(Array.isArray(list) ? list : []);
        })
        .catch(() => { if (mounted) setNotifs([]); });
    };
    load();
    const timer = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(timer); };
  }, [space]);

  // Quand l'utilisateur ouvre la page visée par une notification, elle devient lue.
  useEffect(() => {
    const apiObj = NOTIFICATIONS_API[space];
    const hasUnreadMatch = notifs.some((n) => !n.readAt && n.link === location.pathname);
    if (!hasUnreadMatch || !apiObj?.readNotifications) return;
    apiObj
      .readNotifications(location.pathname)
      .then(() => {
        setNotifs((prev) =>
          prev.map((x) => (x.link === location.pathname ? { ...x, readAt: x.readAt || new Date().toISOString() } : x))
        );
      })
      .catch(() => {});
  }, [location.pathname, notifs, space]);

  const openNotification = async (n) => {
    setNotifOpen(false);
    const apiObj = NOTIFICATIONS_API[space];
    try {
      await apiObj?.readNotifications?.();
    } catch {
      /* ignore */
    }
    setNotifs((prev) => prev.map((x) => (x.readAt ? x : { ...x, readAt: new Date().toISOString() })));
    navigate(n.link || notificationsPath);
  };

  useEffect(() => {
    if (!notifOpen) return undefined;
    const onPointer = (e) => {
      if (!notifRef.current?.contains(e.target)) setNotifOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setNotifOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [notifOpen]);
  const initials = (user?.name || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onPointer = (e) => {
      if (!userMenuRef.current?.contains(e.target)) setUserMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className={`dash-shell dash-shell--${space}`}>
      <aside className={`dash-sidebar ${open ? 'is-open' : ''}`}>
        <div className="dash-brand">
          <span className="dash-brand-mark">T</span>
          <div>
            <strong>Tarjemlek</strong>
          </div>
          <button type="button" className="dash-sidebar-close" onClick={() => setOpen(false)} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <nav className="dash-nav" aria-label={s.title}>
          {items.map(({ to, end, icon: Icon, label, labelKey }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `dash-nav-link${isActive ? ' is-active' : ''}`}
              onClick={() => setOpen(false)}
            >
              <Icon size={18} />
              <span>{label || s[labelKey] || labelKey}</span>
            </NavLink>
          ))}
        </nav>


      </aside>

      {open && <button type="button" className="dash-backdrop" aria-label="Close menu" onClick={() => setOpen(false)} />}

      <div className="dash-main">
        <header className="dash-topbar">
          <button type="button" className="dash-menu-btn" onClick={() => setOpen(true)} aria-label="Menu">
            <Menu size={18} />
          </button>
          <div className="dash-topbar-spacer" aria-hidden="true" />

          {notificationsPath && (
            <div className="dash-notifications-wrap" ref={notifRef}>
              <button
                type="button"
                className="dash-notifications"
                aria-label="Notifications"
                onClick={() => setNotifOpen((v) => !v)}
              >
                <Bell size={20} />
                {notifCount > 0 && <span className="dash-notifications-badge">{notifCount}</span>}
              </button>
              {notifOpen && (
                <div className="dash-notif-dropdown">
                  <div className="dash-notif-dropdown-head">Notifications</div>
                  <div className="dash-notif-dropdown-list">
                    {notifs.length === 0 ? (
                      <p className="dash-notif-empty">{d.empty}</p>
                    ) : (
                      notifs.slice(0, 8).map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          className={`dash-notif-item${n.readAt ? '' : ' is-unread'}`}
                          onClick={() => openNotification(n)}
                        >
                          <strong>{n.title}</strong>
                          {n.body && <p>{n.body}</p>}
                          <small>{n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}</small>
                        </button>
                      ))
                    )}
                  </div>
                  <Link to={notificationsPath} className="dash-notif-more" onClick={() => setNotifOpen(false)}>
                    Tout voir
                  </Link>
                </div>
              )}
            </div>
          )}

          <div className="dash-user-menu" ref={userMenuRef}>
            <button
              type="button"
              className={`dash-user-trigger${userMenuOpen ? ' is-open' : ''}`}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen((v) => !v)}
            >
              <span className="dash-user-avatar" aria-hidden="true">{initials}</span>
              <span className="dash-user-meta">
                <strong>{user?.name || '—'}</strong>
                <small>{roleLabel}</small>
              </span>
              <ChevronDown size={15} className="dash-user-caret" />
            </button>

            {userMenuOpen && (
              <div className="dash-user-dropdown" role="menu">
                <div className="dash-user-dropdown-head">
                  <span className="dash-user-avatar dash-user-avatar--lg" aria-hidden="true">{initials}</span>
                  <div>
                    <strong>{user?.name}</strong>
                    <small>{user?.email || roleLabel}</small>
                    <span className="dash-role-pill">{roleLabel}</span>
                  </div>
                </div>

                <div className="dash-user-dropdown-list">
                  {space === 'admin' ? (
                    <>
                      <Link
                        to="/admin/profile"
                        role="menuitem"
                        className="dash-user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <UserRound size={16} />
                        {s.navProfile || d.profile || 'Mon profil'}
                      </Link>
                      <Link
                        to="/admin/settings"
                        role="menuitem"
                        className="dash-user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings size={16} />
                        {s.navSettings || d.settings || 'Paramètres'}
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        className="dash-user-dropdown-item"
                        onClick={() => { setUserMenuOpen(false); setPasswordOpen(true); }}
                      >
                        <Lock size={16} />
                        {t.auth.changePassword || 'Changer le mot de passe'}
                      </button>
                      <Link
                        to="/"
                        role="menuitem"
                        className="dash-user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <ArrowLeft size={16} />
                        {d.backSite || 'Voir le site'}
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        className="dash-user-dropdown-item dash-user-dropdown-item--danger"
                        onClick={handleLogout}
                      >
                        <LogOut size={16} />
                        {d.logout}
                      </button>
                    </>
                  ) : (
                    <>
                      {PROFILE_PATH[space] && (
                        <Link
                          to={PROFILE_PATH[space]}
                          role="menuitem"
                          className="dash-user-dropdown-item"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <UserRound size={16} />
                          {s.navProfile || d.profile}
                        </Link>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        className="dash-user-dropdown-item"
                        onClick={() => { setUserMenuOpen(false); setPasswordOpen(true); }}
                      >
                        <Lock size={16} />
                        {t.auth.changePassword || 'Changer le mot de passe'}
                      </button>
                      <Link
                        to={items[0]?.to || '/'}
                        role="menuitem"
                        className="dash-user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Home size={16} />
                        {s.navHome || d.overview}
                      </Link>
                      <Link
                        to="/"
                        role="menuitem"
                        className="dash-user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <ArrowLeft size={16} />
                        {d.backSite}
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        className="dash-user-dropdown-item dash-user-dropdown-item--danger"
                        onClick={handleLogout}
                      >
                        <LogOut size={16} />
                        {d.logout}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="dash-content">
          <Outlet />
        </div>

        <DashModal
          open={passwordOpen}
          title={t.auth.changePassword || 'Changer le mot de passe'}
          onClose={() => setPasswordOpen(false)}
        >
          <PasswordChangeForm />
        </DashModal>
      </div>
    </div>
  );
}
